import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@/config/antd";
import { useDispatch } from "react-redux";
import { updateSubEvent } from "@/store/redux/slices/event/sub_event_slice";
import Button from "@/components/common/Button";
import { format } from "date-fns";
import {
  CloseOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
} from "@/config/antd";
import Input from "@/components/common/inputs/Input";
import ImagePicker from "@/components/specific/Image_Picker";
import TimeRangeInput from "@/components/specific/Time_Range_Input";
import Calendar from "@/components/specific/Calender";
import WebIcon from "@/components/common/Web_Icons";
import { useLoader } from "@/hooks/LoaderProvider";
import DOMPurify from "dompurify";

const ModifySubEvent = ({ isVisible, onClose, subEvent, mode, onSuccess }) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  // Initialize state for calendar
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [isSelectingEndDate, setIsSelectingEndDate] = useState(false);

  // Add separate state for time
  const [timeRange, setTimeRange] = useState({
    fromTime: "",
    toTime: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    eventType: "",
    location: {
      type: "",
      address: "",
      virtualService: "",
      url: "",
    },
    duration: {
      startDate: null,
      endDate: null,
      startTime: "",
      endTime: "",
    },
    members: [],
    eventID: "",
    organizationID: "",
    manifestoID: "",
  });

  // Populate form data and time when subEvent changes
  useEffect(() => {
    if (subEvent) {
      const startDate = subEvent.duration.startDate
        ? new Date(subEvent.duration.startDate)
        : null;
      const endDate = subEvent.duration.endDate
        ? new Date(subEvent.duration.endDate)
        : null;

      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);

      // Set time values directly from the API response
      setTimeRange({
        fromTime: subEvent.duration.startTime || "",
        toTime: subEvent.duration.endTime || "",
      });

      setFormData({
        _id: subEvent._id,
        name: subEvent.name,
        description: subEvent.description,
        location: {
          type: subEvent.location.type,
          address: subEvent.location.address,
          virtualService: subEvent.location.virtualService || "",
          url: subEvent.location.url || "",
        },
        duration: {
          startDate: startDate,
          endDate: endDate,
          startTime: subEvent.duration.startTime || "",
          endTime: subEvent.duration.endTime || "",
        },
        members: subEvent.members || [],
        eventID: subEvent.eventID,
        organizationID: subEvent.organizationID,
        manifestoID: subEvent.manifestoID,
      });
    }
  }, [subEvent]);

  const handleSave = async () => {
    showLoader();
    try {
      console.log("Starting save operation...");

      // Sanitize description
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      }).trim();

      // Format dates
      const formatDate = (date) => {
        return date ? format(date, "yyyy-MM-dd") : "";
      };

      const updateData = {
        _id: formData._id,
        name: formData.name,
        description: sanitizedDescription,
        duration: {
          startDate: formatDate(formData.duration.startDate),
          endDate: formatDate(formData.duration.endDate),
          startTime: formData.duration.startTime,
          endTime: formData.duration.endTime,
        },
        location: {
          type: formData.location.type,
          address: formData.location.address,
          virtualService: formData.location.virtualService,
          url: formData.location.url,
        },
        members: formData.members,
        eventID: formData.eventID,
        organizationID: formData.organizationID,
        manifestoID: formData.manifestoID,
      };

      const result = await dispatch(updateSubEvent(updateData)).unwrap();
      console.log("Update result:", result);

      hideLoader();

      // Simply call onSuccess and close the modify dialog
      if (onSuccess) {
        console.log("Calling onSuccess callback...");
        onSuccess();
      }
      onClose(); // Close the modify dialog
    } catch (error) {
      console.error("Failed to update sub event:", error);
      hideLoader();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLocationChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: value,
      },
    }));
  };

  const handleDateSelect = (date) => {
    if (!isSelectingEndDate) {
      setSelectedStartDate(date);
      setIsSelectingEndDate(true);
    } else {
      setSelectedEndDate(date);
      setIsCalendarVisible(false);
      setIsSelectingEndDate(false);

      setFormData((prev) => ({
        ...prev,
        duration: {
          ...prev.duration,
          startDate: selectedStartDate,
          endDate: date,
        },
      }));
    }
  };

  const handleTimeChange = (type, value) => {
    if (type === "fromTime") {
      setTimeRange((prev) => ({ ...prev, fromTime: value }));
      setFormData((prev) => ({
        ...prev,
        duration: { ...prev.duration, startTime: value },
      }));
    } else {
      setTimeRange((prev) => ({ ...prev, toTime: value }));
      setFormData((prev) => ({
        ...prev,
        duration: { ...prev.duration, endTime: value },
      }));
    }
  };

  const eventType = [
    { value: "physical", label: "Physical Event" },
    { value: "virtual", label: "Virtual Event" },
    { value: "physical_and_virtual", label: "Physical and Virtual Event" },
  ];

  const virtualServiceOptions = [
    { value: "third_party", label: "Third Party Service" },
    { value: "vvc", label: "Veegil Virtual Conference (VVC)" },
  ];

  const handleAddImage = (file) => {
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, file],
    }));
  };

  const handleDeleteImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const getDialogTitle = () => {
    return mode === "add" ? "Add New Sub Event" : "Modify Sub Event";
  };

  const getActionButtonText = () => {
    return mode === "add" ? "ADD SUB EVENT" : "UPDATE";
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      width={400}
      closeIcon={null}
      styles={{
        body: { padding: 0 },
        content: {
          padding: 0,
          borderRadius: "16px",
          overflow: "hidden",
        },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
      }}>
      <div className="flex flex-col bg-white">
        {/* Header with title and close button */}
        <div className="p-4 px-6 flex justify-between items-center">
          <h2 className="text-bold-x text-primary">{getDialogTitle()}</h2>
          <CloseOutlined
            onClick={onClose}
            className="cursor-pointer"
            style={{
              color: "var(--veegil-color-primary)",
              backgroundColor: "var(--veegil-color-lighter-blue)",
              borderRadius: "50%",
              padding: "4px",
            }}
          />
        </div>

        {/* Content Area */}
        <div className="flex flex-col px-6 pb-6 space-y-6">
          {/* Sub Event Info Section */}
          <div className="space-y-2">
            <h3 className="text-darkGrey text-regular font-semibold">
              SUB EVENT INFO
            </h3>
            <Input
              label="TITLE"
              placeholder="Enter Title"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
            <Input
              label="DESCRIPTION"
              placeholder="Enter Text"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </div>

          {/* Sub Event Location Section */}
          <div className="space-y-2">
            <h3 className="text-darkGrey text-regular font-semibold">
              SUB EVENT LOCATION
            </h3>

            {/* Location Type Selection */}
            <Input
              label="LOCATION TYPE"
              type="select"
              placeholder="Select location type"
              value={formData.location.type}
              onChange={(e) => handleLocationChange("type", e.target.value)}
              options={eventType}
              icon={
                formData.location.type === "virtual" ? (
                  <VideoCameraOutlined />
                ) : (
                  <EnvironmentOutlined />
                )
              }
            />

            {/* Physical Location Input */}
            {(formData.location.type === "physical" ||
              formData.location.type === "hybrid") && (
              <Input
                label="PHYSICAL ADDRESS"
                placeholder="Enter venue address"
                value={formData.location.address}
                onChange={(e) =>
                  handleLocationChange("address", e.target.value)
                }
                icon={<WebIcon icon="Location" size="small" />}
              />
            )}

            {/* Virtual Location Inputs */}
            {(formData.location.type === "virtual" ||
              formData.location.type === "hybrid") && (
              <>
                <Input
                  label="VIRTUAL SERVICE"
                  type="select"
                  placeholder="Select virtual service"
                  value={formData.location.virtualService}
                  onChange={(e) =>
                    handleLocationChange("virtualService", e.target.value)
                  }
                  options={virtualServiceOptions}
                  icon={<WebIcon icon="Video" size="small" />}
                />

                {formData.location.virtualService &&
                  formData.location.virtualService !== "vvc" && (
                    <Input
                      label="MEETING URL"
                      placeholder="Enter meeting URL"
                      value={formData.location.url}
                      onChange={(e) =>
                        handleLocationChange("url", e.target.value)
                      }
                      icon={<WebIcon icon="Link" size="small" />}
                    />
                  )}

                {formData.location.virtualService === "vvc" && (
                  <div className="mt-2">
                    <Button
                      text="SETUP VEEGIL VIRTUAL CONFERENCE"
                      icon={<WebIcon icon="Plus" size="small" />}
                      onClick={() => {
                        /* Handle VVC setup */
                      }}
                      color="primary"
                      className="text-[9px] h-[20px] py-[15px] border border-primary"
                      customBgColor="white"
                      customTextColor="primary"
                      iconPosition="left"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Invite Members Section */}
          <div className="space-y-2">
            <h3 className="text-darkGrey text-regular font-semibold">
              INVITE MEMBERS
            </h3>
            <ImagePicker
              images={formData.images}
              onAddImage={handleAddImage}
              onDeleteImage={handleDeleteImage}
            />
          </div>

          {/* Date and Time Section */}
          <div className="space-y-2">
            <h3 className="text-darkGrey text-regular font-semibold">
              DATE AND TIME
            </h3>

            {/* Date Range Input */}
            <Input
              label="DATE RANGE"
              value={
                selectedStartDate && selectedEndDate
                  ? `${format(selectedStartDate, "MMM dd, yyyy")} - ${format(
                      selectedEndDate,
                      "MMM dd, yyyy"
                    )}`
                  : ""
              }
              placeholder="Select date range"
              readOnly
              icon={<CalendarOutlined />}
              onClick={() => setIsCalendarVisible(true)}
            />

            {isSelectingEndDate && selectedStartDate && (
              <div className="text-sm text-gray-500">
                Start date selected: {format(selectedStartDate, "MMM dd, yyyy")}
              </div>
            )}

            {/* Time Selection */}
            <div className="space-y-2">
              <h3>Event Time</h3>
              <TimeRangeInput
                fromTime={timeRange.fromTime}
                toTime={timeRange.toTime}
                onFromChange={(value) => handleTimeChange("fromTime", value)}
                onToChange={(value) => handleTimeChange("toTime", value)}
              />
            </div>
          </div>
        </div>

        {/* Calendar Modal */}
        <Calendar
          isVisible={isCalendarVisible}
          onClose={() => {
            setIsCalendarVisible(false);
            setIsSelectingEndDate(false);
          }}
          onDateSelect={handleDateSelect}
          selectedDate={
            isSelectingEndDate ? selectedEndDate : selectedStartDate
          }
          initialDate={selectedStartDate || new Date()}
          headerText={
            isSelectingEndDate ? "Select End Date" : "Select Start Date"
          }
        />

        {/* Footer Buttons */}
        <div className="w-full bg-lighterBlue p-4 flex gap-3">
          <Button
            text="CANCEL"
            color="secondary"
            className="flex-1 border border-primary"
            customBgColor="white"
            customTextColor="primary"
            onClick={onClose}
          />
          <Button
            text={getActionButtonText()}
            color="primary"
            className="flex-1 bg-primary text-white"
            customBgColor="primary"
            customTextColor="white"
            onClick={handleSave}
          />
        </div>
      </div>
    </Modal>
  );
};

ModifySubEvent.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  subEvent: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    location: PropTypes.shape({
      type: PropTypes.string,
      address: PropTypes.string,
      virtualService: PropTypes.string,
      url: PropTypes.string,
    }),
    duration: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    }),
    members: PropTypes.array,
    eventID: PropTypes.string,
    organizationID: PropTypes.string,
    manifestoID: PropTypes.string,
  }),
  mode: PropTypes.oneOf(["add", "modify"]),
  onSuccess: PropTypes.func,
};

export default ModifySubEvent;
